import React from "react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { Spinner } from "@/components/ui/loading";

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  widthClass?: string;
  render: (item: T, rowIndex: number) => React.ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRefresh?: () => void;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (columnKey: string) => void;
  getRowKey: (item: T, index: number) => string;
  actionsRenderer?: (item: T) => React.ReactNode;
  emptyMessage?: string;
}

function getSortIcon(active: boolean, descending: boolean | undefined) {
  if (!active) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 text-gray-400" />;
  return descending ? (
    <ArrowDown className="h-3.5 w-3.5 ml-1 text-blue-600" />
  ) : (
    <ArrowUp className="h-3.5 w-3.5 ml-1 text-blue-600" />
  );
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    data,
    columns,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    loading,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    sortBy,
    sortDescending,
    onSort,
    getRowKey,
    actionsRenderer,
    emptyMessage = "No records found",
  } = props;

  return (
    <div className="space-y-3">
      {/* Table */}
      <div className="border rounded-lg overflow-hidden shadow-sm relative bg-white dark:bg-gray-900">
        <Table>
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/50 border-b-2 border-gray-200 dark:border-gray-700">
              <TableHead className="font-semibold w-16 text-gray-700 dark:text-gray-300">#</TableHead>
              {columns.map(col => (
                <TableHead
                  key={col.key}
                  className={`font-semibold text-gray-700 dark:text-gray-300 ${col.widthClass || ""} ${
                    col.sortable ? "cursor-pointer hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors" : ""
                  }`}
                  onClick={() => col.sortable && onSort && onSort(col.key)}
                >
                  <span className="inline-flex items-center">
                    {col.header}
                    {col.sortable && getSortIcon(sortBy === col.key, !!sortDescending && sortBy === col.key ? sortDescending : false)}
                  </span>
                </TableHead>
              ))}
              {actionsRenderer && <TableHead className="text-right font-semibold text-gray-700 dark:text-gray-300">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow 
                key={getRowKey(item, index)} 
                className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all duration-150 border-b border-gray-100 dark:border-gray-800 last:border-0"
              >
                <TableCell className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {(currentPage - 1) * pageSize + index + 1}
                </TableCell>
                {columns.map(col => (
                  <TableCell key={col.key} className="py-3">{col.render(item, index)}</TableCell>
                ))}
                {actionsRenderer && <TableCell className="text-right py-3">{actionsRenderer(item)}</TableCell>}
              </TableRow>
            ))}
            {data.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={columns.length + (actionsRenderer ? 2 : 1)} className="py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <svg className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-10">
            <div className="flex flex-col items-center gap-3">
              <Spinner />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col lg:flex-row items-center justify-between gap-4 px-2 py-3 bg-gray-50/50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Left: Info & Refresh */}
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full lg:w-auto">
          {onRefresh && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={loading}
                className="h-9 px-4 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                title="Refresh data"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                <span className="font-medium">Refresh</span>
              </Button>
              <div className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-600"></div>
            </>
          )}
          
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <span className="text-gray-500 dark:text-gray-500">Page</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100 px-2 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                {currentPage}
              </span>
              <span className="text-gray-500 dark:text-gray-500">of</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{totalPages}</span>
            </div>
            
            <div className="hidden sm:block h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
            
            <div className="text-gray-600 dark:text-gray-400 text-center sm:text-left">
              <span className="text-gray-500 dark:text-gray-500">Showing </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{(currentPage - 1) * pageSize + 1}</span>
              <span className="text-gray-500 dark:text-gray-500"> to </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{Math.min(currentPage * pageSize, totalCount)}</span>
              <span className="text-gray-500 dark:text-gray-500"> of </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span>
            </div>
          </div>
        </div>

        {/* Right: Page Size & Navigation */}
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rows per page:</span>
            <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(Number(value))}>
              <SelectTrigger className="w-[75px] h-9 font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40"
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="hidden md:flex items-center gap-1">
              {(() => {
                let start = Math.max(1, currentPage - 2);
                let end = Math.min(totalPages, currentPage + 2);
                if (currentPage <= 3) {
                  start = 1;
                  end = Math.min(5, totalPages);
                } else if (currentPage >= totalPages - 2) {
                  start = Math.max(1, totalPages - 4);
                  end = totalPages;
                }
                const pageNumbers: (number | string)[] = [];
                for (let i = start; i <= end; i++) pageNumbers.push(i);
                if (start > 1) {
                  pageNumbers.unshift(1);
                  if (start > 2) pageNumbers.splice(1, 0, "...");
                }
                if (end < totalPages) {
                  if (end < totalPages - 1) pageNumbers.push("...");
                  pageNumbers.push(totalPages);
                }
                return pageNumbers.map((p, idx) =>
                  p === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 dark:text-gray-500">...</span>
                  ) : (
                    <Button
                      key={`page-${p}`}
                      variant={currentPage === p ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(p as number)}
                      className={`h-9 w-9 p-0 font-medium transition-all ${
                        currentPage === p 
                          ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm" 
                          : "hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      }`}
                    >
                      {p}
                    </Button>
                  )
                );
              })()}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40"
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
